<template>
  <popup-modal ref="popup">
    <div class="messageBox verify-email-modal verify-transfer-licence-modal">
      <div class="modal-header modal-header-text"><h4 class="modal-title new-header-text">{{ t('4254') }}</h4></div>
      <form @submit.prevent="verify" novalidate>
        <div class="modal-body modal-body-text">
          <div class="modal-body-text-div" v-html="t('4241', [emailAddress])"></div>
          <div class="modal-body-text-div email-text"><span>{{ emailAddress }}</span></div>
          <div class="modal-input" :aria-busy="busy">
            <template v-for="(_, index) in codes" :key="index">
              <span v-if="restricted && index === 4" class="code-separator" aria-hidden="true">-</span>
              <input :id="'code-element-' + (index + 1)" :value="codes[index]"
                :aria-label="t('transfer.digit', [index + 1])" :aria-invalid="invalidIndex === index"
                :data-title="t('transfer.validCode')" data-toggle="tooltip" data-placement="top"
                :class="['verify-email-input', { error: invalidIndex === index }]"
                inputmode="numeric" pattern="[0-9]" maxlength="1" type="text" autocomplete="off"
                @input="advance(index, $event)" @paste.prevent="paste($event)" @keydown="keyDown(index, $event)"
                @focus="$event.target.select()" :disabled="busy || terminal" />
            </template>
          </div>
          <div v-if="restricted" class="transfer-countdown" role="status" :data-transfer-state="displayState">{{ message }}</div>
        </div>
        <div class="modal-footer">
          <div class="button-container"><button id="verify-licence_transfer-button" class="btn btn-default btn-truckscience btn-primary" type="submit" :disabled="busy || terminal"><span>{{ t('4251') }}</span></button></div>
          <div class="message-container" @click="supportClick"><span v-html="supportText"></span></div>
          <div class="get-started-background"></div>
        </div>
      </form>
    </div>
  </popup-modal>
</template>
<script>
import config from '@/services/config';
import globals from '@/services/globals';
import PopupModal from './PopupModal.vue';
import { ref, toRef, computed, onUnmounted, nextTick } from 'vue';
export default {
  name: 'VerifyTransferLicenceModal', components: { PopupModal }, props: { propsForModal: Object },
  setup(props) {
    const opts = toRef(props, 'propsForModal');
    const restricted = !!opts.value.pendingTransfer, emailAddress = opts.value.emailAddress || '';
    const popup = ref(null), codes = ref(Array(restricted ? 8 : 4).fill('')), operating = ref(false), subscribedBusy = ref(false), invalidIndex = ref(-1);
    const busy = computed(() => operating.value || subscribedBusy.value);
    const pending = ref(opts.value.pendingTransfer), now = ref(Date.now()), feedback = ref(null), queuedResend = ref(false);
    const nextSend = ref(restricted ? Date.now() + pending.value.RetryAfterSeconds * 1000 : 0);
    let timer, unsubscribe, promise, resolveModal, settled = false;
    const t = config.getTranslationText;
    const remaining = computed(() => restricted ? Math.max(0, Math.ceil((Date.parse(pending.value.ExpiresUtc) - now.value) / 1000)) : 0);
    const retryIn = computed(() => Math.max(0, Math.ceil((nextSend.value - now.value) / 1000)));
    const terminal = computed(() => restricted && (remaining.value === 0 || !['pending', 'throttled'].includes(pending.value.State)));
    const displayState = computed(() => restricted && remaining.value === 0 ? 'expired' : (terminal.value ? pending.value.State : (feedback.value || 'pending')));
    const message = computed(() => t(displayState.value === 'expired' ? 'transfer.expiryLine' :
      terminal.value || feedback.value === 'failed' ? 'transfer.failureLine' : 'transfer.countdown',
      [Math.floor(remaining.value / 60) + ':' + String(remaining.value % 60).padStart(2, '0')]));
    const supportText = computed(() => t(restricted ? 'transfer.support' : '4252'));
    const input = index => document.getElementById('code-element-' + (index + 1));
    function clearInvalid() {
      if (invalidIndex.value >= 0) window.$(input(invalidIndex.value)).tooltip('destroy');
      invalidIndex.value = -1;
    }
    function invalid(index) {
      clearInvalid(); invalidIndex.value = index;
      nextTick(() => { if (!settled && invalidIndex.value === index) window.$(input(index)).tooltip({ trigger: 'manual', html: false }).tooltip('show'); });
      input(index)?.focus();
    }
    function receive(update) {
      if (settled || !update) return;
      subscribedBusy.value = update.busy === true;
      if (update.error?.cancelled) { settle(update.error); return; }
      if (update.pendingTransfer) {
        if (pending.value && !['pending', 'throttled'].includes(pending.value.State) && ['pending', 'throttled'].includes(update.pendingTransfer.State)) return;
        pending.value = update.pendingTransfer;
        if (update.timing !== false) nextSend.value = Date.now() + pending.value.RetryAfterSeconds * 1000;
      }
      if (update.error) {
        const state = update.error.transferState;
        if (state === 'stale' || state === 'expired') pending.value = { ...pending.value, State: state };
        feedback.value = 'failed'; queuedResend.value = false;
      } else feedback.value = null;
      now.value = Date.now();
      if (terminal.value) queuedResend.value = false;
      if (!busy.value) nextTick(drainResend);
    }
    function settle(result) {
      if (settled) return;
      settled = true; queuedResend.value = false; clearInterval(timer); clearInvalid(); unsubscribe?.(); popup.value?.close(); resolveModal?.(result);
    }
    function show() {
      if (promise) return promise;
      promise = new Promise(resolve => { resolveModal = resolve; });
      if (!restricted) globals.user.updateUser({ licenceTransferCodeRequired: true });
      popup.value.open(); nextTick(() => { if (!settled) input(0)?.focus(); });
      if (restricted) {
        const stop = opts.value.subscribeTransfer?.(receive);
        if (settled) { stop?.(); return promise; }
        unsubscribe = stop;
        timer = setInterval(() => { now.value = Date.now(); if (terminal.value) queuedResend.value = false; drainResend(); }, 250);
      }
      return promise;
    }
    onUnmounted(() => settle({ cancelled: true, unmounted: true }));
    function advance(index, event) {
      if (busy.value || terminal.value || settled) return;
      clearInvalid(); const value = event.target.value;
      if (value.length > 1 && fillCode(value)) return;
      codes.value[index] = value.replace(/[^0-9]/g, '').slice(-1); event.target.value = codes.value[index];
      if (codes.value[index]) (input(index + 1) || document.getElementById('verify-licence_transfer-button'))?.focus();
    }
    function fillCode(value) {
      const digits = value.replace(/[\s\-–—]/g, '');
      if (!(new RegExp('^[0-9]{' + codes.value.length + '}$')).test(digits)) return false;
      clearInvalid(); codes.value = Array.from(digits); document.getElementById('verify-licence_transfer-button')?.focus(); return true;
    }
    function paste(event) {
      if (busy.value || terminal.value || settled) return;
      if (!fillCode(event.clipboardData.getData('text'))) invalid(Math.max(0, codes.value.findIndex(c => !c)));
    }
    function keyDown(index, event) {
      if (busy.value || terminal.value || settled) return;
      if (event.key === 'Backspace') {
        clearInvalid(); if (!codes.value[index] && index > 0) { event.preventDefault(); codes.value[index - 1] = ''; input(index - 1)?.focus(); }
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault(); input(index + (event.key === 'ArrowLeft' ? -1 : 1))?.focus();
      }
    }
    async function run(action, code) {
      if (busy.value || settled || (terminal.value && !['cancel', 'startAgain'].includes(action))) return;
      if (action === 'cancel' || action === 'startAgain') queuedResend.value = false;
      operating.value = true; clearInvalid();
      try {
        const result = await opts.value.operateTransfer(action, code);
        if (settled) return;
        if (result.completed || result.cancelled) { settle(result); return; }
        if (result.pendingTransfer) {
          receive({ pendingTransfer: result.pendingTransfer });
          if (action === 'resend' && result.pendingTransfer.State === 'throttled') {
            queuedResend.value = true;
            // Preserve the server's wait, with a floor for a zero-second
            // throttle so finally cannot immediately dispatch the same intent.
            nextSend.value = Math.max(nextSend.value, Date.now() + 1000);
          }
          if (action === 'resend' && result.pendingTransfer.State === 'pending') codes.value.fill('');
          if (action === 'verify' && !terminal.value && result.pendingTransfer.State === 'pending') invalid(0);
        } else receive({ error: { transferState: 'failed' } });
      } catch (error) {
        if (error?.transferBusy) return;
        if (error?.cancelled) settle(error); else receive({ error });
      }
      finally { operating.value = false; drainResend(); }
    }
    function verify() {
      if (busy.value || terminal.value || settled) return;
      const code = codes.value.join('');
      if (!(restricted ? /^[0-9]{8}$/ : /^[0-9]{4}$/).test(code)) { invalid(Math.max(0, codes.value.findIndex(c => !c))); return; }
      if (restricted) return run('verify', code);
      const result = { Action: 'verify', Code: code }; opts.value.callbackForVerification(result); settle(result);
    }
    function drainResend() {
      if (!queuedResend.value || busy.value || settled || terminal.value || feedback.value === 'failed' || retryIn.value > 0) return;
      queuedResend.value = false; return run('resend');
    }
    function resendEmail() {
      if (restricted) {
        if (settled || terminal.value || feedback.value === 'failed' || queuedResend.value) return;
        queuedResend.value = true; return drainResend();
      }
      const result = { Action: 'resendEmail' }; opts.value.callbackForVerification(result); settle(result);
    }
    function startAgain() {
      queuedResend.value = false;
      if (restricted) return run('startAgain');
      const result = { Action: 'startAgain' }; opts.value.callbackForVerification(result); settle(result);
    }
    function supportClick(event) {
      const id = event.target.closest('a')?.id;
      if (!['resendEmail', 'startAgain', 'cancelTransfer'].includes(id)) return;
      event.preventDefault();
      if (id === 'resendEmail') return resendEmail();
      if (busy.value) return;
      if (id === 'startAgain') return startAgain();
      queuedResend.value = false; return run('cancel');
    }
    return { show, popup, codes, restricted, emailAddress, busy, terminal, remaining, retryIn, message, displayState, supportText, invalidIndex,
      queuedResend, advance, paste, keyDown, run, verify, resendEmail, startAgain, supportClick, t };
  }
};
</script>
